/**
 * What anything on the table is, in words: the tooltip for a thing you point
 * at whether or not you can act on it. The board stays uncluttered — a
 * province shows its medallion and its pieces — and the detail is one hover
 * away: its roads, whose pieces those are, what its tile is for.
 */
import { MANIFESTS, senateVotes, type ActionKind, type PlayerId, type View } from '@trajan/engine';
import { groupCards } from '../cards/cards';
import { bonusCondition, forumName, forumUse, trajanName, trajanReward } from '../panel/describe';
import { argOf, kindOf, type TargetId } from '../table/ids';
import { lapStep } from '../table/layout';
import { trayDetail } from './offer';

export interface Note {
  title: string;
  lines: string[];
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];
const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const list = (xs: string[]) =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
const provinceName = (id: string) =>
  id === 'camp' || id === 'italia'
    ? MANIFESTS.camp
    : (MANIFESTS.provinces.find((p) => p.id === id)?.name ?? id);

/** The note for target `id`, as seen by `seat` (whose tableau is on show). */
export function inspect(
  view: View,
  seat: PlayerId,
  id: TargetId,
  nameOf: (seat: PlayerId) => string,
): Note | null {
  const arg = argOf(id);
  const player = view.players[seat];
  switch (kindOf(id)) {
    case 'time': {
      const step = lapStep(view.time);
      return {
        title: `Quarter ${view.time.quarter} of ${MANIFESTS.timeTrack.quarters}, round ${view.time.round} of ${MANIFESTS.timeTrack.roundsPerQuarter}`,
        lines: [
          `${view.time.length - step} more time ends the round.`,
          'Every marker moved costs one time. A demand is revealed after rounds 1, 2 and 3.',
        ],
      };
    }
    case 'demand': {
      const d = view.demands[Number(arg)];
      return d
        ? {
            title: `The people demand ${d.icon}`,
            lines: [
              'Meet it at the quarter’s end with a matching tile, or lose points for every demand missed.',
            ],
          }
        : { title: 'A demand still to come', lines: ['Revealed at the end of a round.'] };
    }
    case 'forum': {
      const t = view.board.forum[Number(arg)];
      return t
        ? { title: capitalise(forumName(t)), lines: [capitalise(forumUse(t, view)) + '.'] }
        : { title: 'An empty forum space', lines: ['Refilled at the end of the quarter.'] };
    }
    case 'yellow': {
      const t = view.board.yellowSpaces[Number(arg)];
      return t
        ? {
            title: `${capitalise(t.action)} extra-action tile`,
            lines: [
              `Keep it: after a later ${t.action} action, spend it to take that action again.`,
            ],
          }
        : { title: 'An empty extra-action space', lines: ['Refilled at the end of the quarter.'] };
    }
    case 'province':
    case 'italia': {
      const pid = id === 'italia' ? 'camp' : arg;
      const p = MANIFESTS.provinces.find((q) => q.id === pid);
      const live = p ? view.board.provinces[p.id] : undefined;
      const roads = p ? p.adjacent : MANIFESTS.campAdjacent;
      const lines = [`Roads to ${list(roads.map(provinceName))}.`];
      if (p && pid !== 'camp' && MANIFESTS.campAdjacent.includes(p.id))
        lines[0] += ` And to ${MANIFESTS.camp}.`;
      if (live?.tile)
        lines.push(`Holds a ${forumName(live.tile)}: the first leader here seizes it.`);
      const legion = live?.legionnaires ?? [];
      if (legion.length) lines.push(`Occupied by ${list(legion.map(nameOf))}.`);
      const leaders = view.players.filter((q) => q.leader === pid).map((q) => nameOf(q.id));
      if (leaders.length)
        lines.push(`${list(leaders)}’s leader${leaders.length > 1 ? 's are' : ' is'} here.`);
      return {
        title: p ? `${p.name} · ${p.vp} VP` : `${MANIFESTS.camp} — the military camp`,
        lines,
      };
    }
    case 'site': {
      const i = Number(arg);
      const site = view.board.sites[i];
      const cols = MANIFESTS.constructionGrid.columns;
      const where = `Row ${Math.floor(i / cols) + 1}, column ${(i % cols) + 1}.`;
      const lines = [where];
      if (site?.workers.length) lines.push(`Built on by ${list(site.workers.map(nameOf))}.`);
      const bonus = site?.tile ? MANIFESTS.firstOfTypeBonus[site.tile.icon] : undefined;
      if (bonus)
        lines.push(`A player’s first ${site!.tile!.icon} also gives a free ${bonus} action.`);
      return {
        title: site?.tile ? `${capitalise(site.tile.icon)} · ${site.tile.vp} VP` : 'A built site',
        lines,
      };
    }
    case 'stack': {
      const i = Number(arg);
      const stack = view.board.trajanStacks[i] ?? [];
      const top = stack[0];
      const kind = MANIFESTS.tiles.trajanStacks[i] ?? '';
      return top
        ? {
            title: `${capitalise(trajanName(top.kind))} · ${top.vp} VP`,
            lines: [
              `Needs ${top.colors[0]} and ${top.colors[1]} markers in the tray beside its slot.`,
              `Reward: ${trajanReward(top)}.`,
              `${stack.length} ${kind} tile${stack.length === 1 ? '' : 's'} in this stack.`,
            ],
          }
        : { title: `The ${kind} stack is empty`, lines: [] };
    }
    case 'senate': {
      const i = Number(arg);
      const sp = MANIFESTS.senateTrack.spaces[i];
      const discs = view.board.senateStacks[i] ?? [];
      return {
        title:
          i === 0
            ? 'The senate’s start space'
            : `Senate space ${sp?.vp}: ${sp?.vp} VP, ${sp?.votes} votes`,
        lines: discs.length ? [`Discs here: ${list(discs.map(nameOf))}, bottom to top.`] : [],
      };
    }
    case 'bonus': {
      const side = Number(arg);
      const t = view.board.senateBonus[side];
      return t
        ? {
            title: `Bonus tile: ${bonusCondition(t, 'yellow')}`,
            lines: [
              `${t.yellow} VP a time on its yellow side, taken by the consul; ${t.grey} on its grey side, by the vice-consul.`,
              'Scores at the end of the game.',
            ],
          }
        : { title: 'Taken', lines: ['Refilled at the end of the quarter.'] };
    }
    case 'ship': {
      const ship = view.board.ships.find((s) => s.id === arg);
      const table = MANIFESTS.ships.find((s) => s.id === arg);
      if (!ship || !table) return null;
      const side = ship.side === 'colour' ? table.colour : table.grey;
      const wants: Record<string, string> = {
        identical: 'up to four cards of one commodity',
        different: 'up to four different commodities',
        pairs: 'one, two or three pairs',
      };
      return {
        title: `The ${table.set} ship${ship.side === 'grey' ? ', grey side' : ''}`,
        lines: [
          `Takes ${wants[table.set]}.`,
          Object.entries(side)
            .map(([n, vp]) => `${n} → ${vp} VP`)
            .join(' · '),
          ...(ship.side === 'colour' ? ['Turns grey once used this quarter.'] : []),
        ],
      };
    }
    case 'deck':
      return {
        title: `${view.hiddenCounts.drawPile} cards to draw`,
        lines: ['The seaport action draws two and discards one.'],
      };
    case 'pile': {
      const pile = view.discards[Number(arg)] ?? [];
      const top = pile[pile.length - 1];
      return {
        title: top ? `${capitalise(top.type)} on top` : 'An empty discard pile',
        lines: [
          `${pile.length} card${pile.length === 1 ? '' : 's'} here. The top one can be taken at the seaport.`,
        ],
      };
    }
    case 'tray': {
      const i = Number(arg);
      const tray = player?.trays[i] ?? [];
      const action = MANIFESTS.trayActions[i] as ActionKind;
      return {
        title: `${capitalise(action)} tray`,
        lines: [
          tray.length ? `Holds ${list(tray)}.` : 'Empty.',
          ...(player && tray.length ? [capitalise(trayDetail(player, i)) + '.'] : []),
        ],
      };
    }
    case 'slot': {
      const i = Number(arg);
      const tile = player?.trajanSlots[i] ?? null;
      const arch =
        player?.arch === i ? ' The Arch marks it: the next Trajan tile taken goes here.' : '';
      return tile
        ? {
            title: `Slot ${ROMAN[i]}: ${trajanName(tile.kind)}, ${tile.vp} VP`,
            lines: [
              `Complete it with ${tile.colors[0]} and ${tile.colors[1]} in the ${MANIFESTS.trayActions[i]} tray, landing your last marker there.`,
              `Reward: ${trajanReward(tile)}.`,
            ],
          }
        : { title: `Slot ${ROMAN[i]}: empty`, lines: arch ? [arch.trim()] : [] };
    }
    case 'plus2': {
      const has = player?.plus2Spaces.includes(arg as ActionKind) ?? false;
      return {
        title: `${capitalise(arg)} extra-action space`,
        lines: [
          has
            ? `A “+2” marker is here: spending a ${arg} extra-action tile gives two ${arg} actions.`
            : 'A “+2” marker put here makes each extra action of this kind count twice.',
        ],
      };
    }
    case 'marker':
      return null;
    case 'vp':
      return player
        ? {
            title: `${player.vp} victory points`,
            lines: [
              `${senateVotes(player)} votes at the balance of power: the senate track and senate tiles.`,
            ],
          }
        : null;
    case 'supply':
      return player
        ? {
            title: `${player.supply} tokens in the supply`,
            lines: ['Deployed as workers or legionnaires.'],
          }
        : null;
    case 'camp':
      return player
        ? arg === 'legion'
          ? {
              title: `${player.legionnaires} legionnaire${player.legionnaires === 1 ? '' : 's'} in camp`,
              lines: ['Occupying a province takes one from here, where the leader stands.'],
            }
          : {
              title: `${player.workers} worker${player.workers === 1 ? '' : 's'} in camp`,
              lines: ['Building takes one from here onto a site in the district.'],
            }
        : null;
    case 'held':
      return player ? heldNote(view, player, arg) : null;
    case 'card': {
      const card = [...(player?.hand ?? []), ...(player?.display ?? [])].find((c) => c.id === arg);
      if (!card) return null;
      const onDisplay = player?.display.some((c) => c.id === arg) ?? false;
      const same = (player?.hand ?? []).filter((c) => c.type === card.type).length;
      return {
        title: capitalise(card.type),
        lines: [
          onDisplay
            ? 'On display: it scores at the end of the game.'
            : `${same} ${card.type} in hand.`,
        ],
      };
    }
    case 'display': {
      const groups = groupCards(player?.display ?? []);
      return {
        title: `${player?.display.length ?? 0} cards on display`,
        lines: [
          groups.length
            ? list(groups.map((g) => (g.count > 1 ? `${g.count} ${g.type}` : g.type))) + '.'
            : 'Nothing yet.',
          'Displayed cards count for bonus tiles at the end of the game.',
        ],
      };
    }
  }
  return null;
}

function heldNote(view: View, player: View['players'][number], id: string): Note | null {
  const forum = player.forumTiles.find((t) => t.id === id);
  if (forum)
    return {
      title: capitalise(forumName(forum)),
      lines: [capitalise(forumUse(forum, view)) + '.'],
    };
  const kept = player.trajanDemandTiles.find((t) => t.id === id);
  if (kept) {
    return {
      title: `Trajan demand tile: ${kept.icon ?? ''}`.trim(),
      lines: ['Meets one demand of its kind at every quarter’s end.'],
    };
  }
  const extra = player.extraActionTiles.find((t) => t.id === id);
  if (extra) {
    return {
      title: `${capitalise(extra.action)} extra-action tile`,
      lines: [`After a ${extra.action} action, spend it to take that action again.`],
    };
  }
  const built = player.constructionTiles.find((t) => t.id === id);
  if (built) {
    const same = player.constructionTiles.filter((t) => t.icon === built.icon);
    return {
      title: `${same.length} ${built.icon}${same.length === 1 ? '' : 's'} built`,
      lines: ['Different buildings score together as sets at the end of the game.'],
    };
  }
  const bonus = player.bonusTiles.find((t) => t.id === id);
  if (bonus) {
    return {
      title: `Bonus tile, ${bonus.side} side`,
      lines: [capitalise(bonusCondition(bonus, bonus.side)) + ', at the end of the game.'],
    };
  }
  return null;
}
